import React, { useState } from 'react';
import { addHours } from 'date-fns';
import { isEmpty } from 'lodash';
import { fieldLabelText } from '@repo/lib';
import { ChevronRight } from 'lucide-react';

import Dash from '@dashboard/components/Dash';
import FieldEditDialog from '@dashboard/components/FieldEditDialog';
import { formatDateTime } from '@dashboard/lib/format-date';
import getAbandonmentReminderText from '@dashboard/lib/getAbandonmentReminderText';
import getEmailRecoveryStage from '@dashboard/services/abandonment/getEmailRecoveryStage';
import { getDataFieldTypeLabel } from '@dashboard/services/pageFieldTypes/dataFieldTypes';
import renderFieldValue from '@dashboard/services/pageFieldTypes/renderFieldValue';
import {
  AddressLines,
  getAddressLines,
  getAddressRows,
} from '@dashboard/components/AddressSummary';
import MultilineText from '@dashboard/components/MultilineText';
import OrderCardMenu from './OrderCardMenu';
import { OrderCard, OrderTableCard, TitleLink } from './OrderCard';
import OrderDetailsTable from './OrderDetailsTable';
import { guestReminderRows } from './guestReminderText';

/** Cart abandonment: the same four rows and settings link as before, in a detail table. */
export const OrderAbandonmentCard = ({ record, seller }) => {
  if (record.isAbandoned !== true) return null;
  const stage = getEmailRecoveryStage(record.queryParameters, record.recoveredAt);
  return (
    <OrderTableCard
      title="Cart abandonment"
      action={<TitleLink to="/settings/cart-abandonment">Abandonment settings</TitleLink>}
      rows={[
        {
          label: 'Recovered',
          value: record.recoveredAt ? formatDateTime(record.recoveredAt) : <Dash />,
        },
        { label: 'Recovered by', value: stage ?? <Dash /> },
        { label: 'Reminder email', value: getAbandonmentReminderText(seller, record) },
        {
          label: 'Abandoned',
          value: record.createdAt ? formatDateTime(addHours(record.createdAt, 1)) : <Dash />,
        },
      ]}
    />
  );
};

// Once every attendee is added the card stays for the reminders already sent, without the next one.
export const OrderGuestRemindersCard = ({ guests, action, onOpenSent }) => {
  if (!guests?.reminders || !guests.rows?.length) return null;
  const sent = guests.reminders.sentCount ?? 0;
  const complete = !guests.missingCount;
  if (complete && !sent) return null;
  const reminderRows = guestReminderRows(guests.reminders)
    .filter(row => !complete || row.label !== 'Next reminder')
    .map(row => {
      if (row.label !== 'Reminders sent') return row;
      return {
        ...row,
        ...(complete && { value: String(sent) }),
        ...(sent > 0 &&
          onOpenSent && {
            onOpen: onOpenSent,
            trail: <ChevronRight className="size-4 text-muted-foreground" aria-hidden="true" />,
          }),
      };
    });
  return (
    <OrderTableCard
      title="Attendee reminders"
      action={complete ? null : action}
      rows={[
        {
          label: 'Attendees missing',
          value: complete ? 'None' : `${guests.missingCount} of ${guests.rows.length}`,
          copy: null,
        },
        ...reminderRows,
      ]}
    />
  );
};

/** License key as a detail table; Enable or Disable sits in the heading's three-dot menu. */
export const OrderLicenseKeyCard = ({ licenseKey, onToggle, toggling }) => {
  if (!licenseKey?.key) return null;
  return (
    <OrderTableCard
      title="License key"
      action={
        <OrderCardMenu
          items={[
            {
              label: licenseKey.enabled ? 'Disable' : 'Enable',
              onSelect: onToggle,
              disabled: toggling,
            },
          ]}
        />
      }
      rows={[
        {
          label: 'Key',
          value: <span className="font-mono">{licenseKey.key}</span>,
          copy: licenseKey.key,
        },
        { label: 'Uses', value: licenseKey.uses },
        { label: 'Status', value: licenseKey.enabled ? 'Enabled' : 'Disabled' },
      ]}
    />
  );
};

/** Fields: non-address fields then the address rows, as before; Edit fields moves into the three-dot menu. */
export const OrderFieldsCard = ({ fields, page, updateFields, groupAddresses = true }) => {
  const [editing, setEditing] = useState(false);
  if (!fields?.length) return null;
  const rows = [
    ...fields
      .filter(field => !groupAddresses || field.element !== 'address')
      .map(field => ({
        key: field._id,
        label: fieldLabelText(field.label),
        sub: getDataFieldTypeLabel(field.type),
        copy: field.value || null,
        value: <MultilineText>{renderFieldValue(field, { icon: false })}</MultilineText>,
      })),
    ...(groupAddresses ? getAddressRows(fields) : []).map((row, index) => ({
      key: `address-${index}`,
      label: row.heading,
      sub: row.subtitle,
      copy: getAddressLines(row.address).join('\n'),
      value: (
        <span className="inline-block text-right">
          <AddressLines address={row.address} />
        </span>
      ),
    })),
  ];
  return (
    <OrderCard
      title="Fields"
      action={
        <OrderCardMenu items={[{ label: 'Edit fields', onSelect: () => setEditing(true) }]} />
      }
    >
      <OrderDetailsTable rows={rows} />
      <FieldEditDialog
        fields={fields}
        page={page}
        updateFields={updateFields}
        trigger={null}
        open={editing}
        onOpenChange={setEditing}
      />
    </OrderCard>
  );
};

export const filterQueryParameters = queryParameters =>
  queryParameters
    ? Object.entries(queryParameters).filter(
        ([name]) => name !== 'previewKey' && name !== 'testing',
      )
    : [];

export const OrderQueryParametersCard = ({ queryParameters }) => {
  const entries = filterQueryParameters(queryParameters);
  if (isEmpty(entries)) return null;
  return (
    <OrderTableCard
      title="Query parameters"
      rows={entries.map(([label, value]) => ({ label, value }))}
    />
  );
};
